import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Flag } from 'lucide-react';
import PageLayout from '@components/layout/PageLayout';
import ListPageHeader from '@components/common/ListPageHeader';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import { RZTable, DateCell } from '@components/common/table';
import ErrorState from '@components/common/ErrorState';
import ConfirmationModal from '@components/common/modals/ConfirmationModal';
import { contractService } from '@services';
import type { PendingScopeContract } from '@app-types/import';
import type { TableColumn } from '@app-types';
import { formatInstantAsDate } from '@utils/dateUtils';
import { showToast } from '@utils/toast';
import { getApiErrorMessage } from '@utils/apiError';
import { getScopeLabel } from '@utils/referenceCatalog';
import styles from './PendingScopeWorklistPage.module.css';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const columns: TableColumn[] = [
  { key: 'contract', label: 'Contract', defaultVisible: true },
  { key: 'supplier', label: 'Supplier', defaultVisible: true },
  { key: 'current', label: 'Current scope', defaultVisible: true },
  { key: 'pending', label: 'Pending scope', defaultVisible: true },
  { key: 'trigger', label: 'Trigger', defaultVisible: true },
  { key: 'detected', label: 'Flagged', defaultVisible: true },
  { key: 'action', label: '', defaultVisible: true, alwaysVisible: true }
];

const PendingScopeWorklistPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [pendingAction, setPendingAction] = useState<{ type: 'apply' | 'dismiss'; contract: PendingScopeContract } | null>(null);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['pending-scope', currentPage, pageSize],
    queryFn: () => contractService.getPendingScopeContracts()
  });

  const contracts = data?.data ?? [];
  const total = data?.total ?? 0;
  const paged = contracts.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['pending-scope'] });
    void queryClient.invalidateQueries({ queryKey: ['contracts'] });
    void queryClient.invalidateQueries({ queryKey: ['program-lead-quick-actions'] });
  };

  const applyMutation = useMutation({
    mutationFn: (contractId: number) => contractService.applyPendingScope(contractId),
    onSuccess: () => {
      showToast.success('Pending scope applied');
      setPendingAction(null);
      invalidate();
    },
    onError: (err: unknown) => {
      showToast.error(getApiErrorMessage(err, 'Failed to apply pending scope'));
    }
  });

  const dismissMutation = useMutation({
    mutationFn: (contractId: number) => contractService.dismissPendingScope(contractId),
    onSuccess: () => {
      showToast.success('Pending scope dismissed');
      setPendingAction(null);
      invalidate();
    },
    onError: (err: unknown) => {
      showToast.error(getApiErrorMessage(err, 'Failed to dismiss pending scope'));
    }
  });

  const renderCell = (column: TableColumn, row: PendingScopeContract) => {
    switch (column.key) {
      case 'contract':
        return (
          <div className={styles.contractCell}>
            <div>{row.contract_number}</div>
            <button
              type="button"
              className={styles.tableLink}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/contracts/${row.id}`);
              }}
            >
              {row.name}
            </button>
          </div>
        );
      case 'supplier':
        if (!row.supplier_name || row.supplier_id == null) return row.supplier_name || '-';
        return (
          <button
            type="button"
            className={styles.tableLink}
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/suppliers/${row.supplier_id}`);
            }}
          >
            {row.supplier_name}
          </button>
        );
      case 'current':
        return <Badge variant="default">{getScopeLabel(row.assessment_scope)}</Badge>;
      case 'pending':
        return <Badge variant="warning">{getScopeLabel(row.pending_assessment_scope)}</Badge>;
      case 'trigger':
        return row.pending_scope_trigger || '-';
      case 'detected':
        return <DateCell date={row.pending_scope_at} formatDate={(date) => formatInstantAsDate(date, 'Unknown')} />;
      case 'action':
        return (
          <div className={styles.actions}>
            <Button
              variant="primary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                setPendingAction({ type: 'apply', contract: row });
              }}
            >
              Apply
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                setPendingAction({ type: 'dismiss', contract: row });
              }}
            >
              Dismiss
            </Button>
          </div>
        );
      default:
        return '-';
    }
  };

  if (error) {
    return (
      <PageLayout>
        <ErrorState message="Failed to load pending scope changes." />
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Pending Scope Changes">
      <ListPageHeader icon={<Flag size={24} />} title="Pending Scope Changes" count={total} />

      <RZTable
        data={paged}
        columns={columns}
        getRowKey={(row) => String(row.id)}
        renderCell={renderCell}
        pagination={{
          currentPage,
          totalPages,
          totalItems: total,
          pageSize,
          onPageChange: setCurrentPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
          itemName: 'contracts',
          pageSizeOptions: PAGE_SIZE_OPTIONS
        }}
        state={{
          isLoading,
          isFetching,
          isEmpty: contracts.length === 0,
          emptyTitle: 'No pending scope changes',
          emptyDescription: 'Calculated scope updates for in-assessment contracts will appear here.',
          loadingMessage: 'Loading pending scope changes...'
        }}
      />

      <ConfirmationModal
        isOpen={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        onConfirm={() => {
          if (!pendingAction) return;
          if (pendingAction.type === 'apply') {
            applyMutation.mutate(pendingAction.contract.id);
          } else {
            dismissMutation.mutate(pendingAction.contract.id);
          }
        }}
        title={pendingAction?.type === 'apply' ? 'Apply pending scope?' : 'Dismiss pending scope?'}
        message={
          pendingAction?.type === 'apply'
            ? `Change ${pendingAction.contract.contract_number} from ${getScopeLabel(pendingAction.contract.assessment_scope)} to ${getScopeLabel(pendingAction.contract.pending_assessment_scope)}.`
            : `Leave ${pendingAction?.contract.contract_number ?? 'this contract'} at ${getScopeLabel(pendingAction?.contract.assessment_scope) || 'its current scope'} and clear the pending flag.`
        }
        confirmLabel={pendingAction?.type === 'apply' ? 'Apply' : 'Dismiss'}
        confirmVariant={pendingAction?.type === 'apply' ? 'primary' : 'danger'}
        isProcessing={applyMutation.isPending || dismissMutation.isPending}
        dataTestId="pending-scope-confirmation-modal"
      />
    </PageLayout>
  );
};

export default PendingScopeWorklistPage;
